import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type {
  AttachmentId,
  DailyInspectionId,
  DailyReportId,
  NonConformityId,
  ProjectId,
  TeamMeasurementId,
  UserId,
} from '../shared/ids';
import { ATTACHMENT_ERROR, ATTACHMENT_ERROR_MESSAGE } from './errors';

/**
 * Anexo da conferência — a evidência do que foi visto (Q16).
 *
 * A foto é o que sobra quando ninguém lembra mais do dia: o encarregado fotografa a frente
 * pelo tablet, e seis meses depois a foto é a única resposta para "estava assim mesmo?".
 *
 * Duas decisões moram aqui:
 *
 * - **o metadado é separado do binário** (ADR-109). Nome, tipo, tamanho, autor e data ficam
 *   em `attachments`; os bytes ficam no `FileStorage`, endereçados por `blobKey`. Listar os
 *   anexos de uma conferência não pode carregar dez megabytes de imagem para desenhar dez
 *   miniaturas;
 * - **o tipo é decidido pelo conteúdo, não pela extensão** (RN-105). `foto.jpg` que na
 *   verdade é um executável renomeado não vira anexo — e a extensão é justamente a parte do
 *   arquivo que qualquer um edita sem esforço.
 */

/**
 * A quem o anexo pertence.
 *
 * Nasceu como união de um membro só, à espera do segundo — e o segundo é o RDO (Fase 18).
 * A foto da conferência é a evidência de **uma pessoa** num dia; a do RDO é o relatório
 * fotográfico **do dia**, e sai no documento que vai para fora. São donos diferentes, e por
 * isso a que já está presa a uma conferência fica onde está: migrá-la seria reescrever
 * histórico para ganhar nada.
 */
export type AttachmentEntityType =
  | 'DAILY_INSPECTION'
  | 'DAILY_REPORT'
  | 'NON_CONFORMITY'
  /**
   * A foto da medição da equipe (Fase 30, D7): a frente × serviço que a equipe mediu no dia.
   * Na obra por equipe a quantidade não é de uma pessoa, e a evidência dela também não.
   */
  | 'TEAM_MEASUREMENT';

/** O id do dono — opaco; quem diz de que tipo ele é é o `entityType` ao lado. */
export type AttachmentOwnerId =
  DailyInspectionId | DailyReportId | NonConformityId | TeamMeasurementId;

export interface Attachment {
  readonly id: AttachmentId;
  readonly projectId: ProjectId;
  readonly entityType: AttachmentEntityType;
  readonly entityId: AttachmentOwnerId;
  /** Endereço do binário no `FileStorage` — nunca os bytes. */
  readonly blobKey: string;
  readonly originalName: string;
  readonly mimeType: SupportedMimeType;
  /** Tamanho do arquivo escolhido pelo usuário, antes da compressão. */
  readonly sizeBytes: number;
  /** Tamanho realmente armazenado. Igual ao original quando não há o que comprimir. */
  readonly compressedSizeBytes: number;
  readonly width?: number;
  readonly height?: number;
  readonly checksum: string;
  /**
   * A legenda, obrigatória no RDO (RN-214).
   *
   * No relatório fotográfico ela é o que diz **o que se está vendo** — `FABRICAÇÃO DE FORMA
   * DE ESCADA` embaixo da foto. Sem ela a segunda página é um mosaico de imagens que só
   * quem esteve lá reconhece, e o documento existe justamente para quem não esteve.
   *
   * Na conferência continua ausente: lá a foto já está pendurada na pessoa e no dia, e a
   * galeria diz de quem é sem precisar de texto.
   */
  readonly caption?: string;
  readonly uploadedByUserId: UserId;
  readonly uploadedAt: string;
  /**
   * Arquivado: o binário saiu do banco, o registro ficou.
   *
   * A galeria continua mostrando que a foto existiu e quando saiu — o contrário seria
   * indistinguível de uma conferência feita sem foto nenhuma.
   */
  readonly archivedAt?: string;
  /** Exclusão é soft delete: o anexo sai da galeria e o histórico continua íntegro. */
  readonly deletedAt?: string;
}

// ── Limites (RN-105) ────────────────────────────────────────────────────────────

/** 10 MB. O celular do canteiro fotografa em 3–5 MB; o dobro disso é folga suficiente. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * Dez por dono. Mais que isso não é evidência, é despejo de câmera.
 *
 * O mesmo número serve à conferência e ao RDO: dez fotos são a página do relatório
 * fotográfico — o RDO de referência tem seis.
 */
export const MAX_ATTACHMENTS_PER_ENTITY = 10;

/** Maior lado da imagem depois da compressão — legível no tablet, leve na cota. */
export const MAX_IMAGE_DIMENSION = 1600;

/** Qualidade do JPEG gerado na compressão. */
export const IMAGE_QUALITY = 0.8;

export type SupportedMimeType = 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';

/** O que o seletor de arquivos oferece — conveniência, nunca validação. */
export const ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';

export function isImage(mimeType: SupportedMimeType): boolean {
  return mimeType !== 'application/pdf';
}

// ── Magic bytes (RN-105) ────────────────────────────────────────────────────────

/**
 * Assinatura de cada formato aceito.
 *
 * `undefined` numa posição é curinga: o WebP tem quatro bytes de tamanho entre `RIFF` e
 * `WEBP`, e eles variam com o arquivo.
 */
interface Signature {
  readonly mimeType: SupportedMimeType;
  readonly bytes: readonly (number | undefined)[];
}

const SIGNATURES: readonly Signature[] = [
  { mimeType: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { mimeType: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  {
    mimeType: 'image/webp',
    // "RIFF" ---- "WEBP"
    bytes: [
      0x52,
      0x49,
      0x46,
      0x46,
      undefined,
      undefined,
      undefined,
      undefined,
      0x57,
      0x45,
      0x42,
      0x50,
    ],
  },
  { mimeType: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46, 0x2d] },
];

function matches(bytes: Uint8Array, signature: Signature): boolean {
  if (bytes.length < signature.bytes.length) return false;
  return signature.bytes.every(
    (expected, index) => expected === undefined || bytes[index] === expected,
  );
}

/**
 * O tipo real do arquivo, lido dos primeiros bytes.
 *
 * `undefined` = nenhum formato aceito reconhecido. Não existe "provavelmente é uma imagem":
 * o que não é reconhecido não entra.
 */
export function sniffMimeType(bytes: Uint8Array): SupportedMimeType | undefined {
  return SIGNATURES.find((signature) => matches(bytes, signature))?.mimeType;
}

// ── Validação ───────────────────────────────────────────────────────────────────

export interface AttachmentCandidate {
  readonly fileName: string;
  /** O tipo que o navegador declarou. Serve de pista, nunca de autoridade. */
  readonly declaredMimeType: string;
  readonly bytes: Uint8Array;
}

export interface ValidatedAttachment {
  readonly mimeType: SupportedMimeType;
  readonly sizeBytes: number;
}

/**
 * O arquivo pode virar anexo? (RN-105)
 *
 * A ordem importa. O tamanho é verificado antes do conteúdo porque um arquivo de 400 MB
 * não deve ser inspecionado byte a byte só para ser recusado depois; e o conteúdo é
 * verificado antes de qualquer escrita, porque recusar depois de gravar deixaria lixo no
 * armazenamento de quem já está com a cota apertada.
 *
 * Divergência entre o tipo declarado e o real **não** é recusada por si só: o navegador
 * erra o `type` de arquivos vindos de aplicativos de câmera com frequência. O que vale é o
 * conteúdo — e é ele que decide o `mimeType` gravado.
 */
export function validateAttachment(
  candidate: AttachmentCandidate,
  existingCount: number,
): Result<ValidatedAttachment, DomainError> {
  if (existingCount >= MAX_ATTACHMENTS_PER_ENTITY) {
    return err(
      domainError(
        ATTACHMENT_ERROR.TOO_MANY_FILES,
        ATTACHMENT_ERROR_MESSAGE[ATTACHMENT_ERROR.TOO_MANY_FILES]!,
        { existingCount, limit: MAX_ATTACHMENTS_PER_ENTITY },
      ),
    );
  }

  const sizeBytes = candidate.bytes.byteLength;
  if (sizeBytes === 0) {
    return err(
      domainError(
        ATTACHMENT_ERROR.FILE_EMPTY,
        ATTACHMENT_ERROR_MESSAGE[ATTACHMENT_ERROR.FILE_EMPTY]!,
        { fileName: candidate.fileName },
      ),
    );
  }

  if (sizeBytes > MAX_ATTACHMENT_BYTES) {
    return err(
      domainError(
        ATTACHMENT_ERROR.FILE_TOO_LARGE,
        ATTACHMENT_ERROR_MESSAGE[ATTACHMENT_ERROR.FILE_TOO_LARGE]!,
        { fileName: candidate.fileName, sizeBytes, limit: MAX_ATTACHMENT_BYTES },
      ),
    );
  }

  const mimeType = sniffMimeType(candidate.bytes);
  if (mimeType === undefined) {
    // Quem escolheu algo que nem se dizia foto recebe "formato não aceito"; quem escolheu
    // um arquivo que **se dizia** foto e não é recebe "o conteúdo não corresponde" — são
    // enganos diferentes, e mandar os dois para a mesma frase ajuda menos. A mensagem diz
    // o que fazer: "magic bytes inválidos" não significa nada para quem está no canteiro.
    const declaredAsAccepted =
      candidate.declaredMimeType.startsWith('image/') ||
      candidate.declaredMimeType === 'application/pdf';
    const code = declaredAsAccepted
      ? ATTACHMENT_ERROR.CONTENT_MISMATCH
      : ATTACHMENT_ERROR.UNSUPPORTED_TYPE;

    return err(
      domainError(code, ATTACHMENT_ERROR_MESSAGE[code]!, {
        fileName: candidate.fileName,
        declared: candidate.declaredMimeType,
      }),
    );
  }

  return ok({ mimeType, sizeBytes });
}

// ── Checksum ────────────────────────────────────────────────────────────────────

/**
 * FNV-1a de 32 bits sobre os bytes.
 *
 * Mesma escolha do checksum do backup, e pela mesma razão: detectar **corrupção**, não
 * adulteração. Não há adversário numa foto que o próprio usuário tirou, e depender de
 * `crypto.subtle` tornaria a função assíncrona e indisponível fora de contexto seguro.
 */
export function attachmentChecksum(bytes: Uint8Array): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < bytes.length; index += 1) {
    hash ^= bytes[index]!;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * Endereço do binário no armazenamento.
 *
 * Leva o id do anexo, e não o nome do arquivo: dois `foto.jpg` na mesma conferência são
 * o caso comum, e a câmera do Android nomeia tudo igual.
 */
export function attachmentBlobKey(id: AttachmentId): string {
  return `attachment/${id}`;
}

export function createAttachment(input: {
  id: AttachmentId;
  projectId: ProjectId;
  entityType: AttachmentEntityType;
  entityId: AttachmentOwnerId;
  caption?: string;
  originalName: string;
  mimeType: SupportedMimeType;
  sizeBytes: number;
  compressedSizeBytes: number;
  width?: number;
  height?: number;
  checksum: string;
  uploadedByUserId: UserId;
  now: string;
}): Attachment {
  return {
    id: input.id,
    projectId: input.projectId,
    entityType: input.entityType,
    entityId: input.entityId,
    blobKey: attachmentBlobKey(input.id),
    originalName: input.originalName,
    mimeType: input.mimeType,
    sizeBytes: input.sizeBytes,
    compressedSizeBytes: input.compressedSizeBytes,
    checksum: input.checksum,
    uploadedByUserId: input.uploadedByUserId,
    uploadedAt: input.now,
    ...(input.caption === undefined ? {} : { caption: input.caption }),
    ...(input.width === undefined ? {} : { width: input.width }),
    ...(input.height === undefined ? {} : { height: input.height }),
  };
}

/** Quanto texto cabe embaixo de uma foto sem virar parágrafo. */
export const MAX_CAPTION_LENGTH = 120;

/**
 * A legenda da foto (RN-214).
 *
 * Obrigatória no RDO e ausente na conferência — e é o `entityType` que decide, porque a
 * regra não é sobre o arquivo, é sobre onde ele vai aparecer. Texto vazio some em vez de
 * virar string vazia, pelo mesmo motivo da observação do dia: ausência e "escreveram nada"
 * são a mesma coisa, e representá-las de dois jeitos daria duas telas para o mesmo estado.
 */
export function parseCaption(
  caption: string | undefined,
  entityType: AttachmentEntityType,
): Result<string | undefined, DomainError> {
  const texto = caption?.trim() ?? '';

  if (texto === '') {
    if (entityType === 'DAILY_REPORT') {
      return err(
        domainError(
          ATTACHMENT_ERROR.CAPTION_REQUIRED,
          ATTACHMENT_ERROR_MESSAGE[ATTACHMENT_ERROR.CAPTION_REQUIRED]!,
        ),
      );
    }
    return ok(undefined);
  }

  if (texto.length > MAX_CAPTION_LENGTH) {
    return err(
      domainError(
        ATTACHMENT_ERROR.CAPTION_TOO_LONG,
        ATTACHMENT_ERROR_MESSAGE[ATTACHMENT_ERROR.CAPTION_TOO_LONG]!,
        { length: texto.length, max: MAX_CAPTION_LENGTH },
      ),
    );
  }

  return ok(texto);
}

/** Exclusão é soft delete: some da galeria, permanece no histórico. */
export function deleteAttachment(attachment: Attachment, now: string): Attachment {
  return { ...attachment, deletedAt: now };
}

export function isLive(attachment: Attachment): boolean {
  return attachment.deletedAt === undefined;
}

/** O binário ainda está no banco? Arquivado tem registro, não tem bytes (Fase 11). */
export function hasStoredFile(attachment: Attachment): boolean {
  return attachment.deletedAt === undefined && attachment.archivedAt === undefined;
}

/**
 * Quanto esta conferência ocupa de fato — o que a tela mostra ao lado da cota.
 *
 * A foto arquivada não conta: ela já não ocupa nada, e somá-la faria o arquivamento
 * parecer inútil justamente para quem acabou de fazê-lo para liberar espaço.
 */
export function storedBytesOf(attachments: readonly Attachment[]): number {
  return attachments
    .filter(hasStoredFile)
    .reduce((total, attachment) => total + attachment.compressedSizeBytes, 0);
}
